// Formatting + CSV export. buildCSV/esc/BOM ported from L2406-2424; fmtTime
// from L2510. buildCSV and fmtTime are pure and unit-tested; downloadCSV touches
// the DOM and is exercised by the e2e smoke suite.

import type { Site } from './types';

const BOM = '﻿';

export { BOM as CSV_BOM };

/** RFC 4180: quote a value containing a comma, quote, CR, or LF. */
function esc(v: unknown): string {
  const s = String(v == null ? '' : v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function buildCSV<T>(rows: T[], cols: string[], getters: Array<(r: T) => unknown>): string {
  return (
    cols.map(esc).join(',') +
    '\n' +
    rows.map((r) => getters.map((g) => esc(g(r))).join(',')).join('\n')
  );
}

/** Trigger a browser download of CSV text with a UTF-8 BOM (Excel encoding). */
export function downloadCSV(text: string, filename: string): void {
  const blob = new Blob([BOM + text], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    if (a.parentNode) a.parentNode.removeChild(a);
    URL.revokeObjectURL(a.href);
  }, 100);
}

export function exportFilename(date: Date = new Date()): string {
  return `powerhive-solar-sites-${date.toISOString().slice(0, 10)}.csv`;
}

export function exportSitesCsv(rows: Site[]): void {
  const csv = buildCSV(
    rows,
    [
      'Name',
      'Subtitle',
      'Assets',
      'Phase',
      'Area (m²)',
      'Confidence',
      'Latitude',
      'Longitude',
      'kWp',
      'MWh/yr',
      'Has Solar',
      'Has Cabinet',
    ],
    [
      (s: Site) => s.name,
      (s) => s.sub,
      (s) => s.assetLabel,
      (s) => s.phaseLabel,
      (s) => s.area,
      (s) => s.confStr,
      (s) => s.lat,
      (s) => s.lng,
      (s) => s.kwp,
      (s) => s.mwh,
      (s) => (s.hasSolar ? 'Yes' : 'No'),
      (s) => (s.hasCabinet ? 'Yes' : 'No'),
    ],
  );
  downloadCSV(csv, exportFilename());
}

export function fmtKm(km: number): string {
  return km < 10 ? km.toFixed(1) : Math.round(km).toString();
}

export function fmtAgo(ts: number | null | undefined, now = Date.now()): string {
  if (!ts) return '—';
  const mins = Math.round((now - ts) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

/**
 * Normalize Postgres-style timestamps ("2026-06-18 19:15:57.622076+00") into
 * ISO 8601 so Date parses them consistently: space → "T", trim sub-millisecond
 * digits, expand bare "+00" offset to "+00:00". Already-ISO strings pass through.
 */
export function fmtTime(raw: string | null | undefined): string {
  if (!raw) return '';
  const iso = String(raw)
    .replace(' ', 'T')
    .replace(/(\.\d{3})\d+/, '$1')
    .replace(/([+-]\d{2})$/, '$1:00');
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
